/* ============================================================
   DAVID HUFF — Theme JavaScript
   ============================================================ */

'use strict';

// ── Scroll-reveal animations ──────────────────────────────
(function initReveal() {
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('in-view');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0, rootMargin: '0px 0px -48px 0px' });
  document.querySelectorAll('[data-reveal]').forEach(el => io.observe(el));
})();

// ── Sticky header ─────────────────────────────────────────
(function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 40);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

// ── Mobile nav ────────────────────────────────────────────
(function initMobileNav() {
  const openBtn  = document.querySelector('.mobile-menu-btn');
  const closeBtn = document.querySelector('.mobile-nav__close');
  const nav      = document.querySelector('.mobile-nav');
  if (!openBtn || !nav) return;
  const toggle = (open) => {
    nav.classList.toggle('open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    openBtn.setAttribute('aria-expanded', String(open));
  };
  openBtn.addEventListener('click', () => toggle(true));
  closeBtn?.addEventListener('click', () => toggle(false));
  nav.addEventListener('click', e => { if (e.target === nav) toggle(false); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') toggle(false); });
})();

// ── Product gallery thumbnails ────────────────────────────
(function initGallery() {
  const thumbs = document.querySelectorAll('.product-gallery__thumb');
  const main   = document.querySelector('.product-gallery__main img');
  if (!thumbs.length || !main) return;
  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      const src = thumb.querySelector('img')?.src;
      if (src) {
        main.style.opacity = '0';
        main.style.transition = 'opacity 0.25s ease';
        setTimeout(() => { main.src = src; main.style.opacity = '1'; }, 250);
      }
    });
  });
})();

// ── Collection tabs ──────────────────────────────────────
(function initCollectionTabs() {
  const tabs = document.querySelectorAll('.collection-tab');
  if (!tabs.length) return;
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      const target = tab.dataset.target;
      document.querySelectorAll('.collection-panel').forEach(panel => {
        panel.hidden = panel.dataset.panel !== target;
      });
    });
  });
})();

// ── Quantity buttons ─────────────────────────────────────
(function initQty() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const input = btn.closest('[data-qty-wrapper]')?.querySelector('.qty-input');
    if (!input) return;
    const dir = btn.dataset.qty;
    const min = parseInt(input.min || 1);
    const max = parseInt(input.max || 999);
    let val = parseInt(input.value || 1);
    if (dir === 'inc') val = Math.min(val + 1, max);
    if (dir === 'dec') val = Math.max(val - 1, min);
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();

// ── Marquee duplicate for seamless loop ───────────────────
(function initMarquee() {
  document.querySelectorAll('.marquee-track').forEach(track => {
    const clone = track.cloneNode(true);
    track.parentElement.appendChild(clone);
  });
})();

// ── Smooth anchor scrolling ───────────────────────────────
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', e => {
    const target = document.querySelector(anchor.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
});

// ── Video play modal (basic) ──────────────────────────────
(function initVideoCards() {
  document.querySelectorAll('.video-card[data-video-url]').forEach(card => {
    card.addEventListener('click', () => {
      const url = card.dataset.videoUrl;
      if (!url) return;
      const modal = document.createElement('div');
      modal.style.cssText = `
        position:fixed;inset:0;z-index:9999;
        background:rgba(0,0,0,0.92);
        display:flex;align-items:center;justify-content:center;
      `;
      const iframe = document.createElement('iframe');
      iframe.src = url + '?autoplay=1';
      iframe.allow = 'autoplay; fullscreen';
      iframe.style.cssText = 'width:90vw;max-width:1000px;aspect-ratio:16/9;border:none;';
      const close = document.createElement('button');
      close.textContent = '✕';
      close.style.cssText = `
        position:absolute;top:1.5rem;right:1.5rem;
        background:transparent;border:none;color:#fff;
        font-size:1.5rem;cursor:pointer;
      `;
      modal.appendChild(iframe);
      modal.appendChild(close);
      document.body.appendChild(modal);
      document.body.style.overflow = 'hidden';
      const destroy = () => { modal.remove(); document.body.style.overflow = ''; };
      close.addEventListener('click', destroy);
      modal.addEventListener('click', e => { if (e.target === modal) destroy(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') destroy(); }, { once: true });
    });
  });
})();

// ── Notify when added to cart (Shopify AJAX) ─────────────
(function initCartNotify() {
  const notify = (msg) => {
    const toast = document.createElement('div');
    toast.textContent = msg;
    toast.style.cssText = `
      position:fixed;bottom:2rem;left:50%;transform:translateX(-50%);
      background:var(--color-accent);color:var(--color-btn-text);
      font-family:var(--font-heading);font-size:0.875rem;
      font-weight:700;letter-spacing:0.1em;text-transform:uppercase;
      padding:0.875rem 2rem;border-radius:4px;
      box-shadow:0 8px 32px rgba(0,0,0,0.4);
      z-index:9999;
      animation:toastIn 0.35s ease forwards;
    `;
    const style = document.createElement('style');
    style.textContent = `@keyframes toastIn{from{opacity:0;transform:translateX(-50%) translateY(12px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`;
    document.head.appendChild(style);
    document.body.appendChild(toast);
    setTimeout(() => { toast.style.transition = 'opacity 0.3s'; toast.style.opacity = '0'; setTimeout(() => toast.remove(), 300); }, 3000);
  };

  document.querySelectorAll('[data-add-to-cart]').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn = form.querySelector('[data-atc-btn]');
      const originalText = btn?.textContent;
      if (btn) { btn.textContent = 'Adding...'; btn.disabled = true; }
      try {
        const data = new FormData(form);
        const res = await fetch('/cart/add.js', { method: 'POST', body: data });
        if (res.ok) {
          notify('Added to cart!');
          // Update cart count if element exists
          const countEl = document.querySelector('.cart-count');
          if (countEl) {
            const cart = await fetch('/cart.js').then(r => r.json());
            countEl.textContent = cart.item_count;
            countEl.hidden = cart.item_count === 0;
          }
        } else {
          notify('Something went wrong — try again');
        }
      } catch { notify('Something went wrong — try again'); }
      finally {
        if (btn) { btn.textContent = originalText; btn.disabled = false; }
      }
    });
  });
})();
